import type { ExternalLookupAdditionalArgument } from '@/typings';
import type { DropdownInputMode } from '@/ui-kit/dropdown/dropdown';

// The server's PoBoxHelper uses the same pattern; poBox.cases.json drives both test suites.
const PO_BOX =
  /^\s*(?:p\.?\s*o\.?\s*(?:box|b)|post\s+office\s+box|pob)\.?\s*(?:#|no\.?)?\s*-?\s*[0-9]+/i;
// Stops at the marker's "B" while the rest can still become "Box" and a number, so "Pobst Rd" stays a street.
const PO_BOX_START =
  /^\s*(?:p\.?\s*o\.?\s*|post\s+office\s+)b(?:ox?)?\.?(?=$|[\s#\-0-9])/i;

const ALLOW_PO_BOX_KEY = 'allowPoBox';

/** Why a street that takes no PO box refuses the one it holds or is given. */
export const PO_BOX_NOT_ACCEPTED_MESSAGE =
  "PO boxes aren't accepted for this address. Enter a street address.";

/** True when the text starts with a PO box marker followed by a box number. */
export const isPoBox = (text: string): boolean => PO_BOX.test(text);

/** True once the text reaches the "B" of a PO box marker that can still become one. */
export const startsWithPoBox = (text: string): boolean =>
  PO_BOX_START.test(text);

/** True when a street lookup's arguments allow a PO box. */
export const allowsPoBox = (
  args: ExternalLookupAdditionalArgument[] | undefined,
): boolean =>
  args?.some(
    arg => arg.key === ALLOW_PO_BOX_KEY && arg.value.toLowerCase() === 'true',
  ) ?? false;

/** The address typeahead's input mode for a street's text. */
export const poBoxInputMode = (
  text: string,
  allowPoBox: boolean,
): DropdownInputMode => {
  if (allowPoBox && isPoBox(text)) return 'free';
  // isPoBox too: "PO Box No12" is a PO box that startsWithPoBox's lookahead stops short of.
  if (startsWithPoBox(text) || isPoBox(text)) return 'blocked';
  return 'search';
};
